import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/navigation/Header';
import Icon from '../../components/AppIcon';

import { alertService } from '../../services/alertService';
import AlertCard from './components/AlertCard';
import WorkflowEnginePanel from './components/WorkflowEnginePanel';
import LiveMetricsPanel from './components/LiveMetricsPanel';
import AgentAssignmentDashboard from './components/AgentAssignmentDashboard';

const RealTimeAlertCenter = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedUrgency, setSelectedUrgency] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [activePanel, setActivePanel] = useState('alerts');
  const [realtimeChannel, setRealtimeChannel] = useState(null);

  useEffect(() => {
    fetchAlerts();
    fetchStats();

    // Subscribe to real-time alerts
    const channel = alertService?.subscribeToAlerts((data) => {
      if (data?.event === 'INSERT') {
        setAlerts(prev => [data?.alert, ...prev]);
        fetchStats();
      } else if (data?.event === 'UPDATE') {
        setAlerts(prev => prev?.map(a => a?.id === data?.alert?.id ? data?.alert : a));
        fetchStats();
      } else if (data?.event === 'DELETE') {
        setAlerts(prev => prev?.filter(a => a?.id !== data?.alert?.id));
        fetchStats();
      }
    });

    setRealtimeChannel(channel);

    return () => {
      if (realtimeChannel) {
        realtimeChannel?.unsubscribe();
      }
    };
  }, []);

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (selectedUrgency !== 'all') filters.urgency = selectedUrgency;
      if (selectedStatus !== 'all') filters.status = selectedStatus;
      
      const data = await alertService?.getAlerts(filters);
      setAlerts(data);
    } catch (error) {
      console.error('Error fetching alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const statsData = await alertService?.getAlertStats();
      setStats(statsData);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    fetchAlerts();
  }, [selectedUrgency, selectedStatus]);

  const handleAcknowledge = async (alertId) => {
    try {
      await alertService?.acknowledgeAlert(alertId, 'current-user-id');
      fetchAlerts();
      fetchStats();
    } catch (error) {
      console.error('Error acknowledging alert:', error);
    }
  };

  const handleResolve = async (alertId) => {
    try {
      await alertService?.resolveAlert(alertId, 'current-user-id');
      fetchAlerts();
      fetchStats();
    } catch (error) {
      console.error('Error resolving alert:', error);
    }
  };

  const getUrgencyColor = (urgency) => {
    const colors = {
      critical: 'text-destructive',
      high: 'text-warning',
      medium: 'text-primary',
      low: 'text-muted-foreground'
    };
    return colors?.[urgency] || 'text-muted-foreground';
  };

  const getUrgencyBgColor = (urgency) => {
    const colors = {
      critical: 'bg-destructive/10 border-destructive',
      high: 'bg-warning/10 border-warning',
      medium: 'bg-primary/10 border-primary',
      low: 'bg-secondary border-border'
    };
    return colors?.[urgency] || 'bg-secondary border-border';
  };

  const filteredAlerts = alerts?.filter(alert => {
    if (selectedUrgency !== 'all' && alert?.urgency !== selectedUrgency) return false;
    if (selectedStatus !== 'all' && alert?.status !== selectedStatus) return false;
    return true;
  });

  const urgencyLevels = [
    { value: 'all', label: 'All Urgency', count: alerts?.length || 0 },
    { value: 'critical', label: 'Critical', count: stats?.byUrgency?.critical || 0 },
    { value: 'high', label: 'High', count: stats?.byUrgency?.high || 0 },
    { value: 'medium', label: 'Medium', count: stats?.byUrgency?.medium || 0 },
    { value: 'low', label: 'Low', count: stats?.byUrgency?.low || 0 }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Status', count: alerts?.length || 0 },
    { value: 'active', label: 'Active', count: stats?.byStatus?.active || 0 },
    { value: 'acknowledged', label: 'Acknowledged', count: stats?.byStatus?.acknowledged || 0 },
    { value: 'assigned', label: 'Assigned', count: stats?.byStatus?.assigned || 0 },
    { value: 'resolved', label: 'Resolved', count: stats?.byStatus?.resolved || 0 }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="container mx-auto px-4 py-6">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Icon name="AlertTriangle" size={24} className="text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Real-Time Alert Center</h1>
                <p className="text-sm text-muted-foreground">Automated monitoring and intelligent task routing</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-success/10 rounded-lg">
                <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
                <span className="text-xs font-medium text-success">Live Monitoring</span>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Total Alerts</span>
              <Icon name="Bell" size={16} className="text-muted-foreground" />
            </div>
            <p className="text-2xl font-bold text-foreground">{stats?.total || 0}</p>
          </div>
          <div className="bg-card border border-destructive/20 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-destructive">Critical</span>
              <Icon name="AlertCircle" size={16} className="text-destructive" />
            </div>
            <p className="text-2xl font-bold text-destructive">{stats?.byUrgency?.critical || 0}</p>
          </div>
          <div className="bg-card border border-warning/20 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-warning">High</span>
              <Icon name="AlertTriangle" size={16} className="text-warning" />
            </div>
            <p className="text-2xl font-bold text-warning">{stats?.byUrgency?.high || 0}</p>
          </div>
          <div className="bg-card border border-primary/20 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-primary">Active</span>
              <Icon name="Activity" size={16} className="text-primary" />
            </div>
            <p className="text-2xl font-bold text-primary">{stats?.byStatus?.active || 0}</p>
          </div>
          <div className="bg-card border border-success/20 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-success">Resolved</span>
              <Icon name="CheckCircle" size={16} className="text-success" />
            </div>
            <p className="text-2xl font-bold text-success">{stats?.byStatus?.resolved || 0}</p>
          </div>
        </div>

        {/* Panel Navigation */}
        <div className="flex items-center gap-2 mb-6 border-b border-border">
          <button
            onClick={() => setActivePanel('alerts')}
            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
              activePanel === 'alerts' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <div className="flex items-center gap-2">
              <Icon name="Bell" size={16} />
              <span>Active Alerts</span>
            </div>
          </button>
          <button
            onClick={() => setActivePanel('workflow')}
            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
              activePanel === 'workflow' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <div className="flex items-center gap-2">
              <Icon name="GitBranch" size={16} />
              <span>Workflow Engine</span>
            </div>
          </button>
          <button
            onClick={() => setActivePanel('metrics')}
            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
              activePanel === 'metrics' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <div className="flex items-center gap-2">
              <Icon name="BarChart3" size={16} />
              <span>Live Metrics</span>
            </div>
          </button>
          <button
            onClick={() => setActivePanel('agents')}
            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
              activePanel === 'agents' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <div className="flex items-center gap-2">
              <Icon name="Users" size={16} />
              <span>Agent Dashboard</span>
            </div>
          </button>
        </div>

        {/* Active Alerts Panel */}
        {activePanel === 'alerts' && (
          <div>
            {/* Filters */}
            <div className="flex items-center gap-4 mb-6">
              <div className="flex items-center gap-2">
                <Icon name="Filter" size={16} className="text-muted-foreground" />
                <span className="text-sm font-medium text-foreground">Filter by:</span>
              </div>
              <div className="flex items-center gap-2">
                {urgencyLevels?.map(level => (
                  <button
                    key={level?.value}
                    onClick={() => setSelectedUrgency(level?.value)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                      selectedUrgency === level?.value
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                    }`}
                  >
                    {level?.label} ({level?.count})
                  </button>
                ))}
              </div>
              <div className="h-6 w-px bg-border"></div>
              <div className="flex items-center gap-2">
                {statusOptions?.map(status => (
                  <button
                    key={status?.value}
                    onClick={() => setSelectedStatus(status?.value)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                      selectedStatus === status?.value
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                    }`}
                  >
                    {status?.label} ({status?.count})
                  </button>
                ))}
              </div>
            </div>

            {/* Alerts List */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <Icon name="Loader2" size={32} className="animate-spin text-primary mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">Loading alerts...</p>
                </div>
              </div>
            ) : filteredAlerts?.length === 0 ? (
              <div className="bg-card border border-border rounded-lg p-12 text-center">
                <Icon name="CheckCircle" size={48} className="text-success mx-auto mb-4 opacity-50" />
                <h3 className="text-lg font-semibold text-foreground mb-2">No Alerts Found</h3>
                <p className="text-sm text-muted-foreground">All systems operating within normal parameters</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredAlerts?.map(alert => (
                  <AlertCard
                    key={alert?.id}
                    alert={alert}
                    onAcknowledge={handleAcknowledge}
                    onResolve={handleResolve}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Workflow Engine Panel */}
        {activePanel === 'workflow' && <WorkflowEnginePanel />}

        {/* Live Metrics Panel */}
        {activePanel === 'metrics' && <LiveMetricsPanel />}

        {/* Agent Dashboard Panel */}
        {activePanel === 'agents' && <AgentAssignmentDashboard />}
      </div>
    </div>
  );
};

export default RealTimeAlertCenter;